import { NavLink, Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useProject } from '@/api/hooks/useProjects';
import { Skeleton } from '@/components/ui/skeleton';
import { DEPLOYMENT_PARAM } from '@/features/deployments/lib/deploymentState';
import { cn } from '@/lib/utils';

/**
 * The project's tabs. `keepsDeployment` tabs share the `?deployment=` search
 * param, so switching between them keeps the selected deployment.
 */
const tabs = [
  { key: 'config', to: 'config', keepsDeployment: false },
  { key: 'recommendations', to: 'recommendations', keepsDeployment: false },
  { key: 'deployments', to: 'deployments', keepsDeployment: true },
  { key: 'dashboard', to: 'dashboard', keepsDeployment: true },
] as const;

export function ProjectLayout() {
  const { projectId } = useParams<{ projectId: string }>();
  const { t } = useTranslation('projects');
  const { data: project, isLoading } = useProject(projectId);
  const [params] = useSearchParams();
  const deployment = params.get(DEPLOYMENT_PARAM);
  const deploymentQuery = deployment
    ? `?${new URLSearchParams({ [DEPLOYMENT_PARAM]: deployment })}`
    : '';

  return (
    <div className="flex h-full flex-col gap-6">
      <div>
        {isLoading ? (
          <Skeleton className="h-7 w-64" />
        ) : (
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">
            {project?.name ?? 'N/A'}
          </h1>
        )}
        {project?.description && (
          <p className="mt-1 text-sm text-muted-foreground">{project.description}</p>
        )}
      </div>
      <nav className="flex gap-1 overflow-x-auto border-b border-border">
        {tabs.map((tab) => (
          <NavLink
            key={tab.key}
            to={tab.keepsDeployment ? `${tab.to}${deploymentQuery}` : tab.to}
            className={({ isActive }) =>
              cn(
                'relative shrink-0 px-4 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'text-foreground after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:bg-primary'
                  : 'text-muted-foreground hover:text-foreground',
              )
            }
          >
            {t(`tabs.${tab.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}
